import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import css from './SignInForm.css';
import StyledButton from 'common/StyledButton';
import Input from './Input';
import useStarfishAPI from '../hooks/useStarfishAPI';
import useError from '../hooks/useError';
import { SwipeCardConfigs } from "utils/types";
import { isInputFromCardSwipe, keyboardEmulationSwipeCardHandler, signInStudent } from 'utils/kiosk';

function SignInForm(props) {

  // Declarative callbacks for the starfish API
  const [{ data, success, pending, error }, callAPI, { clearError }] = useStarfishAPI();
  const [swipeComplete, setSwipeComplete] = useState(false);
  const [swipePending, setSwipePending] = useState(false);
  
  // Track the id of swipe input timer to clear old timers on new input
  const swipeInputTimer = useRef();
  const inputRef = useRef();

  // Listen to error and call props.setError whenever there is a change
  useError(error, props.setError);

  // Whenever the form is cleared, clear also any errors
  useEffect(() => {
    if ( props.loginId === "" && props.studentLastName === "" ) { clearError(); props.setWarning(null)}
  }, [props.loginId, props.studentLastName, props.setWarning]);
  
  // On page load, add the ExtJS ActiveX listener (not needed once we remove ActiveX)
  useEffect(() => {
    if (!props.swipeCardConfigs.waitingRoomSwipeCardEnabled) return;
    props.loadActiveXListener(
      props.swipeCardConfigs.useNonActiveXMethod,
      props.swipeCardConfigs.waitingRoomSwipeCardParams,
      props.swipeCardConfigs.waitingRoomSwipeCardPattern,
      props.swipeCardConfigs.waitingRoomSwipeCardPrefix,
      props.lastNameSignOnDisabled,
      props.swipeCardConfigs.swipeCardActiveXControlUrl,
      props.setLoginId,
      props.setStudentLastName
    );
    // Unload the listener before leaving the page
    return() => props.unloadActiveXListener(true);
  }, []);
  
  // Send the new last name value to ExtJS/ActiveX on update (not needed once we remove ActiveX)
  useEffect(() => {
    if (!props.swipeCardConfigs.waitingRoomSwipeCardEnabled) return;
    props.updateActiveXLastName(props.studentLastName);
  }, [props.studentLastName]);

  if ( success ) props.onSignIn(data);

  const swipeCardReader = studentIdInput => {
    const { waitingRoomSwipeCardEnabled, waitingRoomSwipeCardUsbParams, waitingRoomSwipeCardParams, 
      waitingRoomSwipeCardPattern, waitingRoomSwipeCardPrefix } = props.swipeCardConfigs;
    
    // If the main swipe card config isn't enabled, simply treat all input as regular typed text
    if ( !waitingRoomSwipeCardEnabled ) {
      props.setLoginId(studentIdInput);
      return;
    }

    // Clear out any timer already set each time we get any new input
    if (swipeInputTimer.current !== null) {
      clearTimeout(swipeInputTimer.current);
      swipeInputTimer.current = null;
    }

    // Prefer the new config, but fall back to the legacy config
    const cardParameters = waitingRoomSwipeCardUsbParams || waitingRoomSwipeCardParams;
    // If the input data doesn't appear to be from swipe, just input it directly as is
    if ( !isInputFromCardSwipe(studentIdInput, cardParameters) ) {
      props.setLoginId(studentIdInput);
      return;
    }
    // We started swipe input, don't allow submit until it is done
    setSwipePending(true);
    setSwipeComplete(false);
    
    // If 2 seconds pass without new input resetting this timer (above), we can consider input complete
    swipeInputTimer.current = setTimeout(  () => {
      try {
        props.setLoginId(keyboardEmulationSwipeCardHandler(studentIdInput, cardParameters, waitingRoomSwipeCardPattern,
          waitingRoomSwipeCardPrefix));
      } catch (e) {
        props.setError(e.message);
      }
      setSwipePending(false);
      setSwipeComplete(true);
    }, 2000);
  };

  const isButtonDisabled = () => pending || !props.loginId || (!props.lastNameSignOnDisabled && !props.studentLastName) || swipePending;

  const onSubmit = async e => {
    if (e) e.preventDefault();
    if ( isButtonDisabled() ) return;
    await signInStudent(callAPI, {
      studentId: props.loginId, 
      lastName: props.studentLastName, 
      kioskId: props.kioskId
    });
  };

  // If there's an error, highlight the student ID to make re-swiping replace it
  const swipeError = () => {
    if (error) {
      inputRef.current.select();
      setSwipeComplete(false);
    }
  };

  useEffect(() => {
    if (swipeComplete) {
      onSubmit();
      swipeError();
    }
  }, [swipeComplete]);

  useEffect(swipeError, [error]);

  return (
    <form className={css['form']} onSubmit={onSubmit}>
      <Input
        id="kiosk-student-id-field"
        autoFocus
        type="password"
        ref={inputRef}
        placeholder={props.studentIdField}
        value={props.loginId}
        label={props.studentIdField}
        onChange={e => swipeCardReader(e.target.value)}
        hint={`Swipe or type your ${props.studentIdField}`}
        errorMessage={error ? `Swipe or type your ${props.studentIdField}` : ""}
      />
      {!props.lastNameSignOnDisabled && <Input
        id="kiosk-last-name-field"
        type="text"
        placeholder={props.lastNameField}
        value={props.studentLastName}
        onChange={e => props.setStudentLastName(e.target.value)}
        label={props.lastNameField}
        hasError={error ? true : false}
      />}
      <StyledButton
        type="submit"
        className={css['signin-button']}
        aria-disabled={isButtonDisabled()}>
        {pending ? "Signing in..." : "Sign in"}
      </StyledButton>
    </form>
  );
}

SignInForm.propTypes = {
  kioskId: PropTypes.number.isRequired,
  onSignIn: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  studentIdField: PropTypes.string.isRequired,
  lastNameField: PropTypes.string.isRequired,
  lastNameSignOnDisabled: PropTypes.bool.isRequired,
  setStudentLastName: PropTypes.func.isRequired,
  studentLastName: PropTypes.string.isRequired,
  setLoginId: PropTypes.func.isRequired,
  loginId: PropTypes.string.isRequired,
  setWarning: PropTypes.func.isRequired,
  swipeCardConfigs: SwipeCardConfigs.isRequired,
  loadActiveXListener: PropTypes.func,
  unloadActiveXListener: PropTypes.func,
  updateActiveXLastName: PropTypes.func
};

export default SignInForm;
